import {Injectable, signal} from '@angular/core';
import {
  BOOKING_CHECKOUT_DRAFT_VERSION,
  BookingCheckoutDraft,
  BookingCheckoutPatch,
} from '@core/models/booking-checkout.types';
import {StorageService} from './storage.service';
import {
  BOOKING_CHECKOUT_STORAGE_KEY,
  fromStoredEnvelope,
  isCheckoutDraftExpired,
  legacyStorageKeysToRemove,
  toStoredEnvelope,
} from '@core/utils/booking-checkout.persistence';

/**
 * Checkout cart for Charge Now / Book Later until payment completes.
 * Survives brief app backgrounding via Ionic Storage (30-minute TTL).
 */
@Injectable({
  providedIn: 'root',
})
export class BookingCheckoutService {
  private readonly draftSignal = signal<BookingCheckoutDraft | null>(null);
  private loadPromise: Promise<void> | null = null;

  /** Read-only reactive snapshot (optional for templates). */
  readonly draft = this.draftSignal.asReadonly();

  constructor(private storageService: StorageService) {
    void this.removeLegacyKeys();
  }

  /** Call once before reading draft on cold start (e.g. page ngOnInit). */
  ensureLoaded(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = this.hydrateFromStorage();
    }
    return this.loadPromise;
  }

  get(): BookingCheckoutDraft | null {
    const draft = this.draftSignal();
    if (draft && isCheckoutDraftExpired(draft.updatedAt)) {
      this.clearLocal();
      return null;
    }
    return draft;
  }

  /** New checkout when the user selects a connector. */
  start(draft: Omit<BookingCheckoutDraft, 'version' | 'updatedAt'>): void {
    this.commit({
      ...draft,
      version: BOOKING_CHECKOUT_DRAFT_VERSION,
      updatedAt: Date.now(),
    });
  }

  patch(partial: BookingCheckoutPatch): void {
    const current = this.get();
    if (!current) {
      return;
    }
    this.commit({
      ...current,
      ...partial,
      updatedAt: Date.now(),
    });
  }

  attachBookingReservation(input: {
    bookingId: string;
    startsAt: string;
    chargingDate?: Date;
    startTime?: number;
  }): void {
    this.patch({
      bookingId: input.bookingId,
      startsAt: input.startsAt,
      chargingDate: input.chargingDate?.toISOString(),
      startTime: input.startTime,
    });
  }

  clear(): void {
    this.clearLocal();
    void this.storageService.remove(BOOKING_CHECKOUT_STORAGE_KEY);
  }

  hasConnectorContext(): boolean {
    const d = this.get();
    return !!(d?.charger?.id && d.connectorNo != null && d.chargerUnitRate != null);
  }

  hasSchedule(): boolean {
    const d = this.get();
    return d?.duration != null && d.duration > 0 && d.unit != null;
  }

  hasPendingBooking(): boolean {
    const d = this.get();
    return typeof d?.bookingId === 'string' && d.bookingId.length > 0;
  }

  private commit(draft: BookingCheckoutDraft): void {
    this.draftSignal.set(draft);
    void this.storageService.set(BOOKING_CHECKOUT_STORAGE_KEY, toStoredEnvelope(draft));
  }

  private clearLocal(): void {
    this.draftSignal.set(null);
  }

  private async hydrateFromStorage(): Promise<void> {
    await this.removeLegacyKeys();
    const raw = await this.storageService.get(BOOKING_CHECKOUT_STORAGE_KEY);
    const restored = fromStoredEnvelope(raw);
    if (restored) {
      this.draftSignal.set(restored);
    }
  }

  private async removeLegacyKeys(): Promise<void> {
    for (const key of legacyStorageKeysToRemove()) {
      await this.storageService.remove(key);
    }
  }
}
